import type { BlockType } from "./blocks";

/**
 * Maps each content block type to the tokens it consumes that are SPECIFIC to that
 * block (or to a small group of related blocks).
 *
 * Common tokens used by every block (typography, spacing, radius, generic backgrounds,
 * `text.primary` etc.) are intentionally NOT listed here — those live in the foundation
 * sections of the design library. The Content Blocks section only surfaces what's
 * distinctive about each block.
 *
 * Source of truth: scanned from `packages/ui-blocks/src/<BlockName>Renderer.tsx`.
 * If you change a renderer's token usage, update this map.
 */
export const BLOCK_TOKEN_USAGE: Partial<Record<BlockType, ReadonlyArray<string>>> = {
  "single-choice-answer": [
    "borders.dropZone",
    "choice.correct.border",
    "choice.correct.bg",
    "choice.drag.border",
    "choice.hover.bg",
    "semantic.success",
    "semantic.error",
    "semantic.errorBg",
    "bar.myOrg",
    "bar.global",
  ],
  categorization: [
    "choice.correct.border",
    "choice.correct.bg",
    "choice.drag.border",
    "choice.hover.bg",
    "choice.wrong.border",
    "choice.wrong.bg",
    "semantic.success",
    "semantic.error",
  ],
  "likert-scale": [
    "likert.negative.strong",
    "likert.negative.medium",
    "likert.negative.light",
    "likert.neutral",
    "likert.positive.light",
    "likert.positive.medium",
    "likert.positive.strong",
  ],
  "workplace-color-spectrum": [
    "wcs.green",
    "wcs.yellow",
    "wcs.orange",
    "wcs.red",
    "wcs.yellowLegend",
  ],
};

/**
 * Course-level completion chrome — tokens that appear across the course / lesson
 * completion experience but aren't owned by a single content block.
 */
export const COMPLETION_CHROME_TOKENS: ReadonlyArray<string> = [
  "completion.accent",
  "completion.label",
  "badge.rosette",
  "badge.star",
  "badge.ribbon",
  "badge.ribbonShadow",
  "badge.size",
];

export function getBlockSpecificTokens(blockType: BlockType): ReadonlyArray<string> {
  return BLOCK_TOKEN_USAGE[blockType] ?? [];
}
